import React, { useState, useEffect, useCallback } from 'react';
import { orderService } from '../services/orderService';
import { customerService } from '../services/customerService';
import { useLocation } from '../contexts/LocationContext';
import { useWarehouse } from '../contexts/WarehouseContext';
import { usePermissions } from '../contexts/PermissionsContext';
import OrdersList from '../components/orders/OrdersList';
import OrderForm from '../components/orders/OrderForm';
import OrderDetails from '../components/orders/OrderDetails';
import OrderStats from '../components/orders/OrderStats';

const OrdersPage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { selectedWarehouse, warehouseId } = useWarehouse();
  const { hasPermission } = usePermissions();

  // Sprawdź uprawnienia do zamówień (zgodnie z PERMISSIONS_SCHEMA)
  const canEditOrders = hasPermission('zamowienia', 'edycja', locationId);
  const canDeleteOrders = hasPermission('zamowienia', 'usuwanie', locationId);
  const canAddOrders = hasPermission('zamowienia', 'tworzenie', locationId);
  
  // Użyj location_id dla adminów, warehouse_id dla pracowników
  const currentLocationId = locationId || warehouseId;

  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Aktywne zakładki i modalne
  const [activeTab, setActiveTab] = useState('list'); // list, stats, new
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  
  // Filtry
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    customer_id: '',
    location_id: currentLocationId || '',
    page: 1
  });
  
  // Statystyki
  const [stats, setStats] = useState({});

  // Aktualizuj filtry gdy zmieni się lokalizacja
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      location_id: currentLocationId || ''
    }));
  }, [currentLocationId]);

  // Załaduj dane przy starcie
  useEffect(() => {
    loadOrders();
    loadStats();
  }, [filters]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await orderService.getOrders({
        ...filters,
        limit: pagination.limit
      });
      
      if (result.success) {
        setOrders(result.data.orders || []);
        setPagination(result.data.pagination || pagination);
      } else {
        setError(result.error || 'Błąd podczas ładowania zamówień');
      }
    } catch (error) {
      setError('Wystąpił błąd podczas ładowania zamówień');
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  const loadStats = useCallback(async () => {
    try {
      const result = await orderService.getOrdersStats(filters.location_id);
      if (result.success) {
        setStats(result.data || {});
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [filters.location_id]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset do pierwszej strony przy zmianie filtrów
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleCreateOrder = () => {
    setEditingOrder(null);
    setShowOrderForm(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setShowOrderForm(true);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zamówienie?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await orderService.deleteOrder(orderId);
      
      if (result.success) {
        showSuccess('Zamówienie zostało usunięte');
        loadOrders();
        loadStats();
      } else {
        setError(result.error || 'Nie udało się usunąć zamówienia');
      }
    } catch (error) {
      setError('Wystąpił błąd podczas usuwania zamówienia');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleOrderSaved = () => {
    setShowOrderForm(false);
    loadOrders();
    loadStats();
    showSuccess(editingOrder ? 'Zamówienie zostało zaktualizowane' : 'Zamówienie zostało utworzone');
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      setLoading(true);
      const result = await orderService.updateOrder(orderId, { status: newStatus });
      
      if (result.success) {
        showSuccess('Status zamówienia został zaktualizowany');
        loadOrders();
        loadStats();
      } else {
        setError(result.error || 'Nie udało się zaktualizować statusu');
      }
    } catch (error) {
      setError('Wystąpił błąd podczas aktualizacji statusu');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  // Mapowanie statusów na kolory
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'var(--warning)';
      case 'processing': return 'var(--info)';
      case 'completed': return 'var(--success)';
      case 'cancelled': return 'var(--danger)';
      default: return 'var(--text-muted)';
    }
  };

  // Mapowanie statusów na etykiety
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Oczekujące';
      case 'processing': return 'W realizacji';
      case 'completed': return 'Zrealizowane';
      case 'cancelled': return 'Anulowane';
      default: return status;
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Nagłówek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Zamówienia</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Zarządzanie zamówieniami | {new Date().toLocaleDateString('pl-PL')}</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canAddOrders && (
              <button className="btn btn-primary" onClick={handleCreateOrder}>
                Nowe zamówienie
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => {
                loadOrders();
                loadStats();
              }}
              disabled={loading}
            >
              {loading ? '⏳' : '🔄'} Odśwież
            </button>
          </div>
        </div>
      </div>

      {/* Komunikaty - styl kompaktowy */}
      {error && (
        <div style={{
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          padding: '0.5rem 0.75rem',
          borderRadius: '4px',
          marginBottom: '0.75rem',
          border: '1px solid var(--danger-light)',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>⚠️ {error}</span>
          <button
            onClick={() => setError('')}
            style={{
              background: 'none',

              color: 'var(--danger)',
              fontSize: '12px',
              padding: '0'
            }}
          >✕</button>
        </div>
      )}

      {successMessage && (
        <div style={{
          backgroundColor: 'var(--success-light)',
          color: 'var(--success)',
          padding: '0.5rem 0.75rem',
          borderRadius: '4px',
          marginBottom: '0.75rem',
          border: '1px solid var(--success-light)',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>✅ {successMessage}</span>
          <button
            onClick={() => setSuccessMessage('')}
            style={{
              background: 'none',

              color: 'var(--success)',
              fontSize: '12px',
              padding: '0'
            }}
          >✕</button>
        </div>
      )}

      {/* Statystyki - kompaktowy styl POS */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '0.75rem',
        flexWrap: 'wrap'
      }}>
        <div style={{
          backgroundColor: 'var(--card-bg)',
          borderLeft: '3px solid var(--info)',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          textAlign: 'center',
          minWidth: '100px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary)' }}>
            {stats.total_orders || pagination.total || 0}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Zamówienia</div>
        </div>

        <div style={{
          backgroundColor: 'var(--card-bg)',
          borderLeft: '3px solid var(--warning)',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          textAlign: 'center',
          minWidth: '100px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--warning)' }}>
            {stats.pending_orders || 0}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Oczekujące</div>
        </div>

        <div style={{
          backgroundColor: 'var(--card-bg)',
          borderLeft: '3px solid var(--cyan)',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          textAlign: 'center',
          minWidth: '100px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--info)' }}>
            {stats.processing_orders || 0}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>W realizacji</div>
        </div>

        <div style={{
          backgroundColor: 'var(--card-bg)',
          borderLeft: '3px solid var(--success)',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          textAlign: 'center',
          minWidth: '100px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--success)' }}>
            {stats.completed_orders || 0}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Zrealizowane</div>
        </div>

        <div style={{
          backgroundColor: 'var(--card-bg)',
          borderLeft: '3px solid var(--purple)',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          textAlign: 'center',
          minWidth: '120px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--purple)' }}>
            {(stats.total_value || 0).toFixed(2)} zł
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wartość</div>
        </div>
      </div>

      {/* Zakładki główne - styl POS */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '4px', backgroundColor: 'var(--border)', padding: '4px', borderRadius: '6px', width: 'fit-content' }}>
          <button
            onClick={() => setActiveTab('list')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: activeTab === 'list' ? '600' : '500',
              backgroundColor: activeTab === 'list' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'list' ? '#fff' : 'var(--text-secondary)'
            }}
          >
            📋 Lista zamówień
            {pagination.total > 0 && (
              <span style={{
                marginLeft: '6px',
                padding: '2px 6px',
                backgroundColor: activeTab === 'list' ? 'rgba(255,255,255,0.2)' : 'var(--border)',
                borderRadius: '10px',
                fontSize: '10px'
              }}>
                {pagination.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: activeTab === 'stats' ? '600' : '500',
              backgroundColor: activeTab === 'stats' ? 'var(--primary)' : 'transparent',
              color: activeTab === 'stats' ? '#fff' : 'var(--text-secondary)'
            }}
          >
            📊 Statystyki
          </button>
        </div>
      </div>

      {/* Zawartość zakładek */}
      {activeTab === 'list' && (
        <OrdersList
          orders={orders}
          loading={loading}
          pagination={pagination}
          filters={filters}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onViewOrder={handleViewOrder}
          onEditOrder={canEditOrders ? handleEditOrder : null}
          onDeleteOrder={canDeleteOrders ? handleDeleteOrder : null}
          onStatusChange={handleUpdateOrderStatus}
          onRefresh={loadOrders}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
        />
      )}

      {activeTab === 'stats' && (
        <OrderStats
          stats={stats}
          loading={loading}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
        />
      )}

      {/* Modal formularza zamówienia */}
      {showOrderForm && (
        <OrderForm
          editingOrder={editingOrder}
          onSave={handleOrderSaved}
          onCancel={() => setShowOrderForm(false)}
        />
      )}

      {/* Modal szczegółów zamówienia */}
      {showOrderDetails && selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrder(null);
          }}
          onEdit={() => {
            handleEditOrder(selectedOrder);
            setShowOrderDetails(false);
          }}
          onStatusChange={handleUpdateOrderStatus}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
        />
      )}
    </div>
  );
};

export default OrdersPage;
